<template>
    <div class="pl--0 pr--0 pt--10 pb--15">
        <template v-if="fetchProjectLoading">
            <PageSpinner />
        </template>

        <template v-else-if="fetchProjectErr">
            <EmptyPage state="error" @action="handleFetchClientProjects" />
        </template>

        <template v-else>
            <div class="flex align-items-center gap-2 flex align-items-center justify-content-between width-100">
                <div v-if="projects.length">
                    <p class="table__striped__footer__results">
                        COUNT: <span>{{ projects.length }}</span>
                    </p>
                </div>
                
                <div class="flex align-items-center">
                    <InnerPageFilter
                        v-if="projects.length > 5"
                        v-model="filter" 
                        page-type="projects" 
                        @update-filter="handleFetchClientProjects"
                    />
                    <DropDownButton 
                        v-if="projects.length"
                        id="clientProjects" 
                        variant="default"
                        variant-style="primary"
                        label="+ Choose project" 
                        class="ml--5"
                        size="sm"
                        :class="{ 'disabled': checkUpgradeLoading && checkUpgradeFields.projects }"
                    >
                        <template #dropdown-content>
                            <ListItem pointer flex @click="initiateCreateProject('existing')">
                                <IconSvg name="folder-open" width="16px" fill="#64748b" />
                                <span class="text--xs text-gray-800 ml--5">Add an existing project</span>
                            </ListItem>

                            <ListItem pointer flex @click="initiateCreateProject('create')">
                                <IconSvg name="folder" width="16px" fill="#64748b" />
                                <span class="text--xs text-gray-800 ml--5"> Add new project</span>
                            </ListItem>
                        </template>
                    </DropDownButton>
                </div>
            </div>

            <div class="w-100 mt--20">
                <template v-if="!projects.length">
                    <EmptyPage
                        state="empty_custom" 
                        btn-state="custom" 
                        icon-name="project"
                        type="project" 
                        position="left"
                        title="No projects with this client" 
                        subtitle="Start an active database of projects associated with this client. To get started, select an existing project or create a new one"
                    >
                        <template #button>
                            <DropDownButton 
                                id="clientProjects" 
                                variant="default"
                                variant-style="primary"
                                label="+ Choose project" 
                                class="ml--5"
                                size="sm"
                                :class="{ 'disabled': checkUpgradeLoading && checkUpgradeFields.projects }"
                            >
                                <template #dropdown-content>
                                    <ListItem pointer flex @click="initiateCreateProject('create')">
                                        <IconSvg name="folder" />
                                        <span class="text--sm text-gray-800 ml--5">Create new project</span>
                                    </ListItem>
                                    <ListItem pointer flex @click="initiateCreateProject('existing')">
                                        <IconSvg name="folder-open" />
                                        <span class="text--sm text-gray-800 ml--5">Add an existing project</span>
                                    </ListItem>
                                </template>
                            </DropDownButton>
                        </template>
                    </EmptyPage>
                </template>

                <template v-else>
                    <div class="page__inner__table">
                        <CustomTable
                            :columns="projectHeaders" 
                            :data="projects"
                            router-name="project-details-view" 
                            custom-action
                            table-type="striped"
                            row-type="btns" 
                            :is-checkbox="false"
                            @onRowClick="goToProjectDetailsPage"
                        >
                            <template #rows="data">
                                <td>
                                    <p class="text--ellipsis title">
                                        {{ data.title || "-" }}
                                    </p>
                                </td>
                                <td>
                                    <Status type="project" :value="data.status" />
                                </td>
                                <td class="text--capitalize">
                                    {{ $format_date_time(data.deadline) || "-" }}
                                </td>
                                <td class="text--capitalize">
                                    {{ data.projectType || "-" }}
                                </td>
                                <td>{{ $format_date(data.createdAt) || "-" }}</td>
                            </template>
    
                            <template #customActionBtns="data">
                                <ListItem pointer flex @click="openProject(data._id)">
                                    <IconSvg name="show" />
                                    <span class="ml--10 text--sm text-gray-800">View project details</span>
                                </ListItem>
                                <ListItem pointer flex @click="startDeleteProject(data._id)">
                                    <IconSvg name="delete" fill="#e04060" />
                                    <span class="ml--10 text--sm text-error-400">Remove project</span>
                                </ListItem>
                            </template>
                        </CustomTable>
                    </div>

                    <div class="table__striped__footer mt--5 pl--0">
                        <Pagination v-if="showPagination" :pagination-data="pageData" />
                    </div>
                </template>
            </div>
        </template>

        <CreateProjectModal 
            :show-modal="showCreateProjectModal" 
            :loading="projectLoading"
            @close="showCreateProjectModal = false" 
            @complete="createProject"
        />

        <FileUploadModal 
            :show="showUploadModal" 
            :mime-types="importType === 'csv' ? 'text/csv' : 'text/json'"
            :file-content="uploadContent" upload-type="projects"
            :is-error="uploadErr" :headers="projectUploadHeaders"
            :progress-value="uploadProgress" @cancel="showUploadModal = false" @update:files="handleFileUpload" 
        />

        <UpgradeAccountModal 
            :show-modal="showUpgradeModal|| showUpgradeModalAlt"  
            type="projects" 
            @cancel="closeUpgradeModal"
        />

        <MainModal 
            :show-modal="showExistingModal"
            size="sm" 
            title="Select an existing project" 
            @close="closeExistingProjectModal"
        >
            <template #body>
                <div class="">
                    <form class="pb--5" @submit.prevent="">
                        <CustomDropdown 
                            id="projectsDropdown" 
                            v-model="form.project" 
                            :options="existingProjects"
                            list-type="projects" 
                            index-label="title" 
                            :loading="searchProjectLoading" 
                            fetch-list-on-show
                            @search="searchProjects" 
                            @select="v => form.project = v" 
                            @fetch-list-show="searchProjects"
                        />
                    </form>
                </div>
            </template>

            <template #footer>
                <div class="w-100 flex justify-content-end align-items-center">
                    <BaseButton
                        label="Cancel" 
                        size="sm" 
                        variant="outline" 
                        :disabled="projectLoading"
                        class="mr--5" 
                        @submit="closeExistingProjectModal"
                    />
                    <BaseButton 
                        label="Add to client" 
                        size="sm" 
                        :loading="projectLoading" 
                        type="submit" 
                        :disabled="!form.project.title"
                        @submit="addExistingProject" 
                    />
                </div>
            </template>
        </MainModal>

        <ConfirmDeletion
            type="project" 
            custom-msg 
            :show-modal="showDeleteModal" 
            :loading="deleteLoading" 
            @cancel="showDeleteModal = false" 
            @delete="removeProject"
        >
            <template #custom-msg>
                <p class="text--sm">
                    Are you sure you want this client removed from the project?
                </p>
            </template>
        </ConfirmDeletion>
    </div>
</template>

<script>
import {
    EmptyPage, 
    MainModal,
    PageSpinner, 
    DropDownButton,
    BaseButton, 
    IconSvg,
    CustomTable, 
    CustomDropdown,
    Status, 
    UpgradeAccountModal,
    Pagination, 
    FileUploadModal,
    CreateProjectModal, 
    ConfirmDeletion,
    FormLabel,
    InnerPageFilter,
    ListItem,
} from "@/components/ui/index";
import { MAX_COUNT_BY_COLLECTIONS } from "@/utils/enums";
import { projectUploadHeaders } from "@/utils/dummy";

export default {
    name: "ClientProjects",

    components: {
        CustomTable, 
        IconSvg, 
        DropDownButton,
        EmptyPage, 
        FileUploadModal, 
        MainModal,
        PageSpinner, 
        CustomDropdown,
        BaseButton, 
        CreateProjectModal, 
        ConfirmDeletion,
        Status, 
        Pagination, 
        UpgradeAccountModal,
        ListItem,
        InnerPageFilter,
    },

    data() {
        return {
            projectUploadHeaders,
            fetchProjectLoading: false,
            fetchProjectErr: false,
            fetchProjectErrMsg: "",
            projectHeaders: [
                { id: "title", accessorKey: "title"},
                { id: "status", accessorKey: "status" },
                { id: "due", accessorKey: "due" },
                { id: "Type", accessorKey: "projectType" },
                { id: "Date added", accessorKey: "createdAt" },
            ],
            showCreateProjectModal: false,
            projectLoading: false,
            deleteLoading: false,
            projectIdToDelete: "",
            importType: "",
            showUploadModal: false,
            uploadProgress: 0,
            uploadContent: [],
            uploadErr: false,
            form: {
                project: {},
            },
            showExistingModal: false,
            searchProjectLoading: false,
            existingProjects: [],
            showDeleteModal: false,
            showUpgradeModalAlt: false,
            filter: {},
        };
    },

    computed: {
        pageData() {
            return this.$store.getters["clients/clientProjects"]?.pageData;
        },

        projects() {
            return this.$store.getters["clients/clientProjects"]?.list;
        },

        showActionBtns() {
            if (this.pageData?.total) {
                return true;
            }

            if (this.isSearched) {
                return true;
            }
            
            return false;
        },

        completedProjectCount() {
            return this.$store.getters["app/completedProjectCount"];
        },

        showUpgradeModal() {
            return this.$store.getters["app/showUpgradeModal"];
        },

        checkUpgradeLoading() {
            return this.$store.getters["app/checkUpgradeLoading"];
        },

        checkUpgradeFields() {
            return this.$store.getters["app/checkUpgradeFields"];
        },

        accountPlan() {
            return this.$store.getters["subscription/subscriptionInfo"]?.plan || "free";
        },

        showPagination() {
            if (this.pageData?.totalPages > 1) {
                return true;
            } else {
                return false;
            }
        },

        isSearched() {
            return Object.keys(this.$route.query).length > 0;
        },

        projectTotal() {
            return this.$store.getters["project/pageData"]?.total;
        },
    },

    async mounted() {
        await this.handleFetchClientProjects();
    },

    methods: {
        async initiateCreateProject(val = "create") {
            try {      
                if (val === "create") {
                    if (["standard", "free"].includes(this.accountPlan)) {        
                        const maxLimit = MAX_COUNT_BY_COLLECTIONS[this.accountPlan].projects.create;
        
                        if (maxLimit !== 0 && this.projectTotal >= maxLimit) {
                            this.showUpgradeModalAlt = true;
                            return;
                        }    
                    }
                }

                if (this.isSearched) {
                    if (["standard", "free"].includes(this.accountPlan)) {        
                        await this.$store.dispatch("app/checkUpgradeStatus", "projects");
                    }
                }

                if (val === "create") {
                    this.showCreateProjectModal = true;
                }

                if (val === "existing") {
                    this.showExistingModal = true;
                }
            } catch (error) {
               
            }
        },

        closeUpgradeModal() {
            if (this.showUpgradeModalAlt) {
                this.showUpgradeModalAlt = false;
                return;
            }
            return this.$store.dispatch("app/closeUpgradeModal", "projects");
        },

        closeExistingProjectModal() {
            this.showExistingModal = false;
            this.form.project = {};
        },

        async handleFetchClientProjects(query) {            
            try {
                this.fetchProjectErr = false;
                this.fetchProjectLoading = true;
                this.filter = query;

                const params = {
                    page: this.pageData?.currentPage || 1,
                    ...query,
                    pageType: "client_projects",
                    status: query?.status?.join(","),
                };

                await this.$store.dispatch("clients/getClientProjects", { id: this.$route.params.id, params });
            } catch (error) {
                this.fetchProjectErr = true;
                
                const errMsg = error?.response?.data?.message || error?.message;
                this.fetchProjectErrMsg = errMsg;
            } finally {
                this.fetchProjectLoading = false;
            }
        },

        async searchProjects(query) {
            try {
                this.searchProjectLoading = true;
                
                const params = {
                    page: 1,
                    title: query,
                    pageType: "ne_client_project",
                };

                const res = await this.$store.dispatch("clients/getClientProjects", { 
                    id: this.$route.params.id, 
                    params, 
                    external: true, 
                });
                
                this.existingProjects = res;
            } catch (error) {
                // console.log({ error });
            } finally {
                this.searchProjectLoading = false;
            }
        }, 

        goToProjectDetailsPage(row) {
            if (!row._id) return;
            this.$router.push(`/dashboard/projects/${row._id}`);
        },

        async addExistingProject() {
            try {                
                this.projectLoading = true;

                await this.$store.dispatch("clients/addExistingroject", { 
                    clientId: this.$route.params.id, 
                    payload: this.form.project, 
                });   

                this.$toast.success("Client added to project");
                this.closeExistingProjectModal();
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;                
                this.$toast.error(errMsg);
            } finally {
                this.projectLoading = false;
            }
        },

        async createProject({ payload, resetFn }) {
            try {                
                this.projectLoading = true;

                const updatedPayload = { ...payload, clientIds: [this.$route.params.id] };
                const resp = await this.$store.dispatch("clients/addNewProject", updatedPayload);   
            
                this.$store.dispatch("project/addProject", { payload, external: true }); // update global project list asynchronously
                
                if (resp.status === "completed") {
                    await this.$store.dispatch("app/fetchCompletedProjectCount", { refresh: true });
                }
                
                if ([5, 10, 25, 50].includes(this.completedProjectCount)) {
                    this.$store.dispatch("app/toggleConfetti", {
                        show: true,
                        type: "rank",
                    });
                }

                if (resetFn) {
                    resetFn();
                }

                this.showCreateProjectModal = false;
                this.$toast.success("Project added to client");
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;                
                this.$toast.error(errMsg);
            } finally {
                this.projectLoading = false;
            }
        },

        startDeleteProject(id) {
            if (!id) {
                this.$toast.error("Missing project id");
            }

            this.currentProjectId = id;
            this.showDeleteModal = true;
        },

        async removeProject() {
            try {                
                this.deleteLoading = true;

                await this.$store.dispatch("clients/deleteProject", { 
                    clientId: this.$route.params.id, 
                    projectId: this.currentProjectId, 
                });   

                this.showDeleteModal = false;
                this.$toast.success("Client removed from project");
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;                
                this.$toast.error(errMsg);
            } finally {
                this.currentProjectId = "";
                this.deleteLoading = false;
            }
        },

        openProject(id) {
            return this.$router.push(`/dashboard/projects/${id}`);
        },

        async setImportType(val) {
            try {
                await this.$store.dispatch("app/checkUpgradeStatus", "projects");
                this.importType = val;
            } catch (error) {
                
            }
        },

        handleFileUpload(files) {
            const file = event?.target?.files[0];

            if (file) {
                this.parseCSVFile(file);
            }
        },

        parseCSVFile(file) {
            Papa.parse(file, {
                header: true,
                complete: this.processCSVData,
                error: this.handleParseError,
            });
        },

        onUploadProgress(event) {
            this.uploadProgress = Math.round((event.loaded * 100) / event.total);
        },

        async processCSVData(results) {
            try {
                this.uploadErr = false;

                const projects = results.data;
                this.uploadContent = projects;

                const formData = new FormData();
                formData.append("projects", JSON.stringify(projects));
                
                await this.$store.dispatch("project/uploadList", { formData, progressCB: this.onUploadProgress });
                
                this.$toast.success("File uploaded successfully");

                this.importType = "";
                this.uploadProgress = 0;
                this.showUploadModal = false;
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;
                
                this.$toast.error(errMsg);
                this.uploadProgress = 0;
                this.uploadErr = true;
            }
        },

        handleParseError(error) {
            console.error("Error parsing CSV file", error);
        },
    },
};
</script>

<style lang="scss" scoped>
    .title {
        max-width: 170px;
    }
</style>